/* global document window */
import * as React from "react";
import { useState, useEffect, useRef, useMemo, memo } from "react";
import HorizontalContextMenuContent from "./HorizontalContextMenuContent/HorizontalContextMenuContent";
import { useSelector } from "react-redux";
import { useGoogleAnalytics } from "@connectaryal/google-analytics";

// Import isSpacerSelected function
const isSpacerSelected = (sel, str) => {
  // Check if the selected column or row is a spacer
  if (sel.columns.length > 0) {
    const col = str.columns[sel.columns[0]];
    return col?.length > 0 ? col[0] === "s" : false;
  }
  if (sel.rows.length > 0) {
    const row = str.rows[sel.rows[0]];
    return row?.length > 0 ? row[0] === "s" : false;
  }
  return false;
};

// Memoize the anySelected function
const anySelected = (sel) => {
  return sel.cells.length > 0 || sel.columns.length > 0 || sel.rows.length > 0 || sel.table;
};

// Memoize the shouldShowMenu function
const shouldShowMenu = (position, selection) => {
  return position && anySelected(selection);
};

// Memoize the menu content component
const MemoizedHorizontalContextMenuContent = memo(HorizontalContextMenuContent);

//context menu that appears when right clicking on the grid/cells
const HorizontalFloatingContextMenu = () => {
  const [position, setPosition] = useState();
  const parentDivRef = useRef(null);
  const [lastSize, setLastSize] = useState({ h: 350, w: 250 });
  const cellEditing = useSelector((state) => state.app.cellEditing);
  const selection = useSelector((state) => state.selection);
  const structure = useSelector((state) => state.gridd.present.structure);
  const uiScale = useSelector((state) => state.preferences.uiScale);
  const contextMenuDeltaX = useSelector((state) => state.preferences.contextMenuDeltaX ?? 15);
  const contextMenuDeltaY = useSelector((state) => state.preferences.contextMenuDeltaY ?? 0);

  // Memoize the show menu calculation
  const showMenu = useMemo(() => shouldShowMenu(position, selection), [position, selection]);
  const { trackEngagement } = useGoogleAnalytics();

  useEffect(() => {
    const showContextMenu = (e) => {
      if (e.altKey) return;

      e.preventDefault();
      if (parentDivRef.current) {
        setLastSize({ h: parentDivRef.current.clientHeight, w: parentDivRef.current.clientWidth });
      }

      const newTop =
        e.clientY + contextMenuDeltaY + lastSize.h <= window.innerHeight
          ? e.clientY + contextMenuDeltaY
          : e.clientY - contextMenuDeltaY - lastSize.h < 0
            ? 0
            : e.clientY - contextMenuDeltaY - lastSize.h;
      const newLeft =
        e.clientX + contextMenuDeltaX + lastSize.w <= window.innerWidth
          ? e.clientX + contextMenuDeltaX
          : e.clientX - contextMenuDeltaX - lastSize.w < 0
            ? 0
            : e.clientX - contextMenuDeltaX - lastSize.w;
      setPosition({ top: newTop, left: newLeft });
      window.contextMenuDiv = parentDivRef.current ? parentDivRef : window.contextMenuDiv;
      trackEngagement({ type: "context_menu" });
    };

    const onClickAnywhere = (e) => {
      const isInvokedFromCode = e.clientX === 0 && e.clientY === 0;
      if (isInvokedFromCode) return;

      const div = parentDivRef.current;
      const left = div?.style?.left ? parseInt(div.style.left.slice(0, div.style.left.length - 2)) : 0;
      const top = div?.style?.top ? parseInt(div.style.top.slice(0, div.style.top.length - 2)) : 0;
      const isClickOutside =
        !div ||
        e.clientX < left ||
        e.clientX > left + div.clientWidth ||
        e.clientY < top ||
        e.clientY > top + div.clientHeight;

      if (isClickOutside) {
        setPosition(undefined);
      }
    };

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        // Don't show menu if a spacer is selected - let App.jsx handle it
        if (isSpacerSelected(selection, structure)) {
          return;
        }
        const mousePos = window.mousePositionRef?.current || { x: 0, y: 0 };
        const eventData = {
          clientX: mousePos.x,
          clientY: mousePos.y,
          preventDefault: () => {},
          altKey: false,
        };
        showContextMenu(eventData);
      }
    };

    document.addEventListener("contextmenu", showContextMenu);
    document.addEventListener("click", onClickAnywhere);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("contextmenu", showContextMenu);
      document.removeEventListener("click", onClickAnywhere);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [cellEditing, selection, structure, contextMenuDeltaX, contextMenuDeltaY]);

  return showMenu ? (
    <div
      ref={parentDivRef}
      style={{
        pointerEvents: "all",
        position: "absolute",
        left: `${position?.left ?? 0}px`,
        top: `${position?.top ?? 0}px`,
        zIndex: 1009,
        transform: `scale(${uiScale ?? 1})`,
      }}
    >
      <MemoizedHorizontalContextMenuContent />
    </div>
  ) : (
    <></>
  );
};

export default memo(HorizontalFloatingContextMenu);
